/**
 * The route outlook: the phase after this one, and what it depends on (D-102).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  IT IS DERIVED BESIDE THE RECOMMENDATION AND NEVER INSIDE IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `Project.recommendationSnapshot` stores a whole `RouteRecommendation`, so a
 *  field added to that type is a field written to a record — and D-102 says the
 *  outlook is shown and stored nowhere. **Keeping it in a separate type is what
 *  makes that structural rather than a rule someone has to remember**: there is
 *  no path from here into the snapshot, because the snapshot's type has no room
 *  for it.
 *
 *  The reason is D-020. A project that holds a planned next phase is one
 *  decision away from starting it, and every screen showing the plan invites
 *  exactly that. The warning that changes nothing would quietly become a queue.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  IT STATES A CONDITION AND NEVER A TIME, AND THAT IS A CORRECTION
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  **D-102 §3 as approved said the duration came from "D-065's approved weekly
 *  rate band applied until the condition band D-071 defines is reached". That
 *  is not derivable and the record is amended.** D-065's rates are *weight*
 *  change per week; D-071's bands are *waist-to-height*. Nothing in this
 *  repository relates the two — `calculations/projection.ts` projects weight
 *  toward a target weight and mentions no waist at all — so turning a rate into
 *  "about two months until the band changes" needs a centimetres-per-kilogram
 *  relationship that **no decision has ever approved**. Inventing one here is
 *  precisely the "number from nowhere" D-101 forbids, and it would sit inside a
 *  sentence the user reads as a plan.
 *
 *  So the outlook names the condition instead of the date: *"once your waist is
 *  below the point where further fat loss stops being worthwhile, Project Sola
 *  would recommend a lean bulk."* It makes no timing claim, so it cannot be
 *  wrong about timing, and it still answers the question that produced D-102 —
 *  that a fat loss phase is not the whole plan and something follows it.
 *
 *  **The user's own horizon is already on screen** as `RouteRecommendation.duration`,
 *  which is arithmetic to a date they typed rather than a forecast. Nothing here
 *  needs to restate it.
 */

import type { PhaseType } from '~/models/phase'
import type {
  ConditionBand,
  CurrentCondition,
  RouteRecommendation,
} from '~/models/routeRecommendation'
import type { RouteEngineThresholds } from '../thresholds'
import { DEFAULT_ROUTE_ENGINE_THRESHOLDS } from '../thresholds'
import { wholeWeeksBetween } from './engine'
import type { RouteInputSnapshot } from './snapshot'
import { applyTrainingFrequencyFloor, chooseRoute } from './table'

/**
 * What Project Sola expects to recommend next, and when it would.
 *
 * **The condition is part of the claim and is never dropped.** A phase named on
 * its own is a promise about the future, which D-068 forbids; a phase named
 * with what it depends on is a statement about the mapping, which is true today
 * and stays true whether or not the user gets there.
 */
export interface RouteOutlook {
  /** The phase the approved mapping gives at `atBand`. Never the current one. */
  readonly nextPhase: PhaseType
  /** The band at which the route changes. */
  readonly atBand: ConditionBand
  /** What has to be true first, in the engine's own vocabulary. */
  readonly condition: string
  /** Why the route changes there. */
  readonly because: string
}

/**
 * Everything the outlook needs, and it is the engine's own input.
 *
 * The training frequency and the event lead time are inputs to D-071's table
 * rather than outputs of it, so they are not on the recommendation and have to
 * come from somewhere. **They come from the same snapshot `recommendRoute`
 * read**, not from parts the caller assembles: a caller computing "weeks to the
 * event" its own way is two implementations of one number, which is the defect
 * `SOLA_TOKEN_HEADER` and the timeout budget were both fixed by removing.
 *
 * **The frequency floor especially must be re-applied.** Without it the outlook
 * could forecast a lean bulk that `recommendRoute` refuses on the day it
 * arrives, which is the one failure mode that would make this actively
 * misleading rather than merely unhelpful.
 */
export interface OutlookInput {
  readonly recommendation: RouteRecommendation
  readonly snapshot: RouteInputSnapshot
}

/**
 * The bands a deficit moves through, leanest last.
 *
 * Ordered rather than compared, because "the next band toward lean" is a
 * property of this three-value sequence and writing it as arithmetic on the
 * ratios would put a fourth reading of the boundaries into the code — the thing
 * `bandFor`'s own comment says it exists to prevent.
 */
const TOWARD_LEAN: readonly ConditionBand[] = ['fatLossPriority', 'middle', 'leanEnough']

/**
 * How the boundary reads to a person, in the vocabulary the table already uses.
 *
 * No ratio appears in this copy. The engine's own reasoning says "at or above
 * half your height" rather than "≥ 0.50", and an outlook that spoke in decimals
 * beside it would read as a different, more clinical claim (D-015).
 */
const REACHING: Readonly<Record<ConditionBand, string>> = {
  fatLossPriority: 'your waist is at or above half your height',
  middle: 'your waist is below half your height',
  leanEnough: 'your waist is well under half your height',
}

/**
 * The phase after this one, or nothing.
 *
 * **Nothing is the common and correct answer**, and it is returned rather than
 * a hedged sentence. Three cases produce it, each for its own reason:
 *
 *  - **The current phase does not move the user toward a boundary.** Only a fat
 *    loss phase does. Maintenance holds position by definition, a lean bulk
 *    moves the other way, and an aesthetic preparation ends at a *date* rather
 *    than at a condition — so for those three, the engine has nothing to say
 *    about a next phase that would not be a guess.
 *  - **The route does not change at any leaner band.** "Maintain my physique"
 *    returns maintenance everywhere, and an outlook saying the plan stays the
 *    same is noise dressed as insight.
 *  - **The recommendation itself is low confidence** (D-102 §5). It is low when
 *    the waist sits within measurement noise of a boundary — which is exactly
 *    when a claim about crossing that boundary is least worth making. **The
 *    outlook may never be more confident than the recommendation it extends.**
 */
export function routeOutlook(
  input: OutlookInput,
  thresholds: RouteEngineThresholds = DEFAULT_ROUTE_ENGINE_THRESHOLDS,
): RouteOutlook | undefined {
  const { recommendation } = input

  if (recommendation.recommendedPhase !== 'fatLoss') return undefined
  if (recommendation.confidence === 'low') return undefined

  for (const band of leanerBandsThan(recommendation.condition.band)) {
    const next = phaseAt(band, input, thresholds)
    if (next === recommendation.recommendedPhase) continue

    return {
      nextPhase: next,
      atBand: band,
      condition: `Once ${REACHING[band]}`,
      because: becauseFor(next, recommendation.condition),
    }
  }

  return undefined
}

/** The bands leaner than this one, nearest first. */
function leanerBandsThan(band: ConditionBand): readonly ConditionBand[] {
  return TOWARD_LEAN.slice(TOWARD_LEAN.indexOf(band) + 1)
}

/**
 * The approved mapping, read at a band the user is not in yet.
 *
 * **This is the whole engine of the feature and it chooses nothing.** D-071's
 * table already says which phase belongs to which outcome in which band; the
 * outlook evaluates it one row over. The training-frequency floor is applied
 * exactly as `recommendRoute` applies it, so a lean bulk the engine would
 * refuse is never forecast.
 */
function phaseAt(
  band: ConditionBand,
  input: OutlookInput,
  thresholds: RouteEngineThresholds,
): PhaseType {
  const { snapshot } = input
  const weeksToEvent = wholeWeeksBetween(snapshot.reference, snapshot.eventDate)

  const chosen = applyTrainingFrequencyFloor(
    chooseRoute({
      desiredOutcome: snapshot.desiredOutcome,
      band,
      weeksToEvent,
      eventHasEnoughLeadTime:
        weeksToEvent !== undefined && weeksToEvent >= thresholds.route.minWeeksForAestheticPrep,
    }),
    snapshot.weeklySessions,
    thresholds.route.minWeeklySessionsForLeanBulk,
  )

  return chosen.phase
}

/**
 * Why the route changes, said once and without a promise.
 *
 * Deliberately short. The reasoning for the *current* phase is the engine's job
 * and is already on the screen above this; repeating it here would give a
 * conditional future the same weight as the present recommendation.
 */
function becauseFor(next: PhaseType, condition: CurrentCondition): string {
  switch (next) {
    case 'maintenance':
      return 'Losing more fat from there tends to cost muscle without changing much, so holding steady becomes the better route.'
    case 'leanBulk':
      return 'Adding size slowly does more from there than losing more would.'
    /* Reachable only if D-071's table changes. Named rather than defaulted, so
       a new destination has to be given its own sentence instead of inheriting
       one that describes something else. */
    case 'fatLoss':
    case 'miniCut':
    case 'aestheticPrep':
      return `Project Sola would reconsider the route once your measurements change from ${condition.band === 'fatLossPriority' ? 'where they are now' : 'their current range'}.`
  }
}
